// Lib
import { get, set, del, keys } from 'idb-keyval';
import * as Immutable from 'immutable';
import { cloneDeep } from 'lodash';

// Utils
import logger from '../../utils/logger';
import { getIsElectron } from '../../hybridApp/hybridAppUtils';
import { getCurrentUserToken } from '../../auth/authService';
import { asObject } from '../../../common/utils/immutableHelper';
import { simpleDecodeJwt } from '../../auth/authTokenClientUtils';
import { manuallyReportError } from '../../analytics/rollbarService';
import { rebuildPersistedState } from './utils/rebuildPersistedState';
import { getUserId, isFeatureEnabled } from '../../../common/users/utils/userPropertyUtils';

// Selectors
import { getCurrentUser } from '../../user/currentUserSelector';

// Config
import getClientConfig from '../../utils/getClientConfig';

// Monitoring
import { NEW_RELIC_EVENT_KEYS, sendNewRelicCustomAttribute } from '../../analytics/newRelicUtils';

// Local Storage
import { getAppLocalCacheResetLocalStorage, setAppLocalCacheResetLocalStorage } from '../appLocalCacheStorageService';

// Constants
import { TIMES } from '../../../common/utils/timeUtil';
import { LOCAL_CACHE_HYDRATION_OMIT } from '../../store/initialStateConstants';
import { ExperimentId } from '../../../common/experiments/experimentsConstants';
import { ROLLBAR_LEVELS } from '../../analytics/rollbarConstants';

const REDUX_STATE_CACHE_KEY = 'redux';

const { appVersion } = getClientConfig();

// using indexed db seems to be failing on incompatible mobile devices, so adding extra check
// Add extra check to ensure the key exists in the cache before trying to delete it
export const SUPPORTS_INDEXED_DB = !getIsElectron() && !!global.indexedDB && typeof global.indexedDB === 'object';

export const clearCachedReduxState = async (): Promise<void> => {
    if (!SUPPORTS_INDEXED_DB) return;

    const cacheKeys = await keys();

    if (!cacheKeys.includes(REDUX_STATE_CACHE_KEY)) return;

    return del(REDUX_STATE_CACHE_KEY).catch((err) => {
        logger.error('Local Cache: Failed to delete redux state from Indexed DB', err);
        manuallyReportError({
            errorMessage: 'Local cache: Failed to delete redux state from Indexed DB',
            level: ROLLBAR_LEVELS.ERROR,
            err,
        });
    });
};

const logRestorePersistedState = (persistedState: object) => {
    const logMessage = '%cLocal Cache: Restoring redux state from local cache';

    if (process.env.NODE_ENV === 'production') return logger.info(logMessage, 'color: blue');

    const deeplyCopiedState = cloneDeep(persistedState);
    return logger.info(logMessage, 'color: blue', deeplyCopiedState);
};

/**
 * Retrieves the redux store from IndexedDB.
 */
export const getCachedReduxState = async (): Promise<Immutable.Map<string, any> | null> => {
    if (!SUPPORTS_INDEXED_DB) {
        logger.info('%cLocal Cache: Not supported in this browser', 'color: blue');
        return null;
    }

    const cacheObject = await get(REDUX_STATE_CACHE_KEY).catch((err) => {
        logger.error('Local Cache: Failed to retrieve redux state from Indexed DB', err);
        manuallyReportError({
            errorMessage: 'Local cache: Failed to retrieve redux state from Indexed DB',
            level: ROLLBAR_LEVELS.WARNING,
            err,
        });
    });

    if (!cacheObject) {
        logger.info('%cLocal Cache: No local cache to restore', 'color: blue');
        return null;
    }

    const shouldResetAppCache = getAppLocalCacheResetLocalStorage() === 'true';

    if (shouldResetAppCache) {
        logger.info('%cLocal Cache: Resetting the redux store cache', 'color: blue');
        await clearCachedReduxState();
        setAppLocalCacheResetLocalStorage('false');
        return null;
    }

    // The controller property is the best way to know if a force refresh has occurred
    // https://developer.mozilla.org/en-US/docs/Web/API/ServiceWorkerContainer/controller
    // Electron app is not using service workers, so we don't need to check for it
    if (!getIsElectron() && navigator?.serviceWorker && navigator?.serviceWorker?.controller === null) {
        logger.info('%cLocal Cache: Force resetting local cache', 'color: blue');
        await clearCachedReduxState();
        return null;
    }

    const version = cacheObject.version;
    const timestamp = cacheObject.timestamp;

    if (!version || !timestamp) {
        logger.warn('Local Cache: No version & no timestamp in the cache', { cache: cacheObject });
        await clearCachedReduxState();
        return null;
    }

    const now = Date.now();

    if (timestamp < now - TIMES.HOUR * 12) {
        logger.warn('Local Cache: The redux store cache has expired', { timestamp, now });
        await clearCachedReduxState();
        return null;
    }

    if (version !== appVersion) {
        logger.warn('Local Cache: The cached state and current app do not match', {
            cachedVersion: version,
            currentAppVersion: appVersion,
        });
        await clearCachedReduxState();
        return null;
    }

    const persistedState = cacheObject.state;

    const currentUser = getCurrentUser(persistedState);

    if (!isFeatureEnabled(ExperimentId.clientLocalCache, currentUser)) {
        logger.warn('Local Cache: The current user is not feature enabled for the local cache', { currentUser });
        await clearCachedReduxState();
        return null;
    }

    const token = getCurrentUserToken();
    const decodedToken = simpleDecodeJwt(token);

    if (!token || !decodedToken) {
        logger.warn('Local Cache: A IndexedDB cache is available but there is no auth token', { currentUser });
        await clearCachedReduxState();
        return null;
    }

    const cachedUserId = getUserId(currentUser);
    const tokenUserId = decodedToken?._id;

    if (cachedUserId !== tokenUserId) {
        logger.warn("Local Cache: The local storage user token's user ID does not match the cached user's user ID", {
            cachedUserId,
            tokenUserId,
        });
        await clearCachedReduxState();
        return null;
    }

    logger.debugGroupCollapsed('%cLocal cache: restoring from cache', 'color: blue; font-weight: normal;');

    logRestorePersistedState(cacheObject);

    const rebuiltState = rebuildPersistedState(persistedState, timestamp, version);

    logger.debugGroupEnd();

    sendNewRelicCustomAttribute(NEW_RELIC_EVENT_KEYS.CLIENT_LOCAL_CACHE_REHYDRATION, true);

    return rebuiltState;
};

/**
 * Remove parts of the state that we don't want to persist to IndexedDB.
 */
const cleanPersistedState = (state: Immutable.Map<string, any>): Immutable.Map<string, any> =>
    state.withMutations((mutableState) => {
        for (const entry of LOCAL_CACHE_HYDRATION_OMIT) {
            const deletionPath = entry.split('.');
            mutableState.deleteIn(deletionPath);
        }
    });

/**
 * Save the redux store to IndexedDB (after it's cleaned and converted to a POJO).
 */
export const setCachedReduxState = async (reduxState: Immutable.Map<string, any>): Promise<void> => {
    if (!SUPPORTS_INDEXED_DB) return;

    const cleanedState = cleanPersistedState(reduxState);
    const pojoState = asObject(cleanedState);

    const timestamp = Date.now();

    const cacheObject = {
        // NOTE: This does not need to be the app version, but for now it seems like the simplest method
        version: appVersion,
        timestamp,
        state: pojoState,
    };

    return set(REDUX_STATE_CACHE_KEY, cacheObject).catch((err) => {
        logger.error('Local Cache: Failed to set redux state to Indexed DB', err);
        manuallyReportError({
            errorMessage: 'Local cache: Failed to set redux state to Indexed DB',
            level: ROLLBAR_LEVELS.ERROR,
            err,
        });
    });
};
